import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Title, Text, Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell, Badge,
  TextInput, Button,
} from '@tremor/react';
import Header from '../components/layout/Header';
import { whatsappAiBotApi, WhatsappAiBotEntry } from '../services/api';

const POLL_INTERVAL_MS = 5000;

function judgeBadge(entry: WhatsappAiBotEntry) {
  if (entry.all_judge_calls_correct === null) {
    return <Badge color="gray">no judge call</Badge>;
  }
  return entry.all_judge_calls_correct ? (
    <Badge color="emerald">judge: correct</Badge>
  ) : (
    <Badge color="red">judge: incorrect</Badge>
  );
}

export default function WhatsappAiBot() {
  const [entries, setEntries] = useState<WhatsappAiBotEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  // Real, direct request (2026-08-25): a full conversation thread can span
  // a real time gap (a clarifying question answered a day later) — the
  // live feed's "last N" slice can miss the earlier turns entirely, so a
  // search hits a SEPARATE endpoint (fetch_whatsapp_ai_bot_thread) scoped
  // to one hm_id, not a client-side filter over what's already loaded.
  const [searchInput, setSearchInput] = useState('');
  const [activeThread, setActiveThread] = useState<string | null>(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');

  const load = useCallback(() => {
    whatsappAiBotApi
      .list()
      .then((res) => setEntries(res.data.entries))
      .catch((err) => setError(err.response?.data?.detail || err.message || 'Failed to load entries'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeThread) return; // paused while viewing a single patient's thread
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load, activeThread]);

  const runSearch = useCallback((hmId: string) => {
    const trimmed = hmId.trim();
    if (!trimmed) return;
    setThreadLoading(true);
    setThreadError('');
    setExpanded(null);
    whatsappAiBotApi
      .thread(trimmed)
      .then((res) => {
        setActiveThread(trimmed);
        setEntries(res.data.entries);
      })
      .catch((err) => setThreadError(err.response?.data?.detail || err.message || 'Failed to load thread'))
      .finally(() => setThreadLoading(false));
  }, []);

  const clearSearch = useCallback(() => {
    setActiveThread(null);
    setSearchInput('');
    setThreadError('');
    setExpanded(null);
    setLoading(true);
    load();
  }, [load]);

  const isBusy = activeThread ? threadLoading : loading;
  const currentError = activeThread ? threadError : error;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="mx-auto max-w-7xl p-6">
        <Card>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <Title>WhatsApp AI Bot Activity</Title>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                runSearch(searchInput);
              }}
            >
              <TextInput
                placeholder="Search by hm_id (e.g. HM-MCB-1234)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-64"
              />
              <Button type="submit" size="xs">Search</Button>
              {activeThread && (
                <Button type="button" variant="secondary" size="xs" onClick={clearSearch}>
                  Back to live feed
                </Button>
              )}
            </form>
          </div>

          {activeThread ? (
            <Text className="mt-2">
              Full thread for <strong>{activeThread}</strong>, oldest first — not limited to the live feed's window
            </Text>
          ) : (
            !isBusy && !currentError && <Text className="mt-2">{entries.length} recent messages · refreshes every 5s</Text>
          )}

          {isBusy && <Text className="mt-6">Loading…</Text>}
          {currentError && <Text className="mt-6 text-red-500">{currentError}</Text>}

          {!isBusy && !currentError && entries.length === 0 && (
            <Text className="mt-6">
              {activeThread ? `No messages found for ${activeThread}.` : 'No messages logged yet.'}
            </Text>
          )}

          {!isBusy && !currentError && entries.length > 0 && (
            <Table className="mt-6">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Time</TableHeaderCell>
                  <TableHeaderCell>Patient</TableHeaderCell>
                  <TableHeaderCell>Message</TableHeaderCell>
                  <TableHeaderCell>Bot Reply</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e, i) => (
                  <React.Fragment key={i}>
                    <TableRow
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpanded(expanded === i ? null : i)}
                    >
                      <TableCell className="whitespace-nowrap align-top text-xs">
                        {e.timestamp || e.logged_at}
                      </TableCell>
                      <TableCell className="align-top text-xs">
                        {e.hm_id || <span className="text-gray-400">unmatched</span>}
                      </TableCell>
                      <TableCell className="align-top max-w-xs">
                        <Text className="text-xs">{e.message}</Text>
                      </TableCell>
                      <TableCell className="align-top max-w-xs">
                        <Text className="text-xs">{e.final_reply}</Text>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex flex-col gap-1">
                          {e.predicted && <Badge color="blue">{e.predicted}</Badge>}
                          <Badge color="gray">{e.final_source}</Badge>
                          {judgeBadge(e)}
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded === i && (
                      <TableRow>
                        <TableCell colSpan={5} className="bg-gray-50">
                          <div className="grid grid-cols-2 gap-4 p-2 text-xs">
                            <div>
                              <Text className="font-semibold">Judge reasoning</Text>
                              <p className="whitespace-pre-wrap text-gray-600">
                                {e.judge_reasoning || '—'}
                              </p>
                              {e.final_answer_reasoning && (
                                <>
                                  <Text className="mt-2 font-semibold">Final-answer re-check</Text>
                                  <p className="whitespace-pre-wrap text-gray-600">
                                    {e.final_answer_correct === false ? 'REJECTED — ' : ''}
                                    {e.final_answer_reasoning}
                                  </p>
                                </>
                              )}
                              {e.your_correction && (
                                <>
                                  <Text className="mt-2 font-semibold">Manual correction</Text>
                                  <p className="text-gray-600">{e.your_correction}</p>
                                </>
                              )}
                            </div>
                            <div>
                              <Text className="font-semibold">Patient data sent to the bot</Text>
                              <pre className="whitespace-pre-wrap break-all text-gray-600">
                                {e.patient_data || '(no patient data — unmatched patient)'}
                              </pre>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </main>
    </div>
  );
}
